import { useContext } from 'react'
import { BuilderContext } from '../../../Builder'
import Box from '@mui/material/Box'
import { Typography, styled } from '@mui/material'
import { getIn } from 'formik'
import { themeUpsertFormKeys } from '../../../../../../../themes-v3/upsert/utils/useThemeValidationSchema'
import { BannerContainerLayout, BannerContainerPosition, ItemStyle } from '@ketch-sdk/ketch-types'
import { SectionEmptyState } from '../../SectionEmptyState'
import { clamp } from 'lodash'
import { BUILDER_CONSTANTS } from '../../../utils/enums'

const buttonsBoxPositionMaxWidthMap = {
  [BannerContainerPosition.Bottom]: '425px',
  [BannerContainerPosition.Top]: '425px',
  [BannerContainerPosition.LeftCorner]: '288px',
  [BannerContainerPosition.RightCorner]: '288px',
  [BannerContainerPosition.BottomMiddle]: '196px',
  [BannerContainerPosition.Center]: '196px',
}

const ButtonsBox = styled(Box)({
  display: 'flex',
  alignItems: 'flex-end',
  gap: '12px',
  height: 'fit-content',
  flexWrap: 'nowrap',
})

const ButtonBox = styled(Box)({
  // Handles button resizing and text wrapping within container
  flex: '1 1 auto',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  alignItems: 'flex-end',
  minWidth: 0,

  // Handles the BottomMiddle and Center layouts where flexDirection === 'column'
  maxWidth: '100%',

  // boxSizing: 'border-box',
  padding: '6.5px 16px',
  gap: '12px',
  height: 'fit-content',
  textAlign: 'center',
})

export const BannerButtons: React.FC = () => {
  const { themeConfig, experienceLayoutConfig, experienceEnglishConfig } = useContext(BuilderContext)

  const layout = getIn(themeConfig, themeUpsertFormKeys.bannerContainerLayout) as BannerContainerLayout
  const position = getIn(themeConfig, themeUpsertFormKeys.bannerContainerPosition) as BannerContainerPosition

  const isCorner = position === BannerContainerPosition.LeftCorner || position === BannerContainerPosition.RightCorner
  const stackButtons =
    (layout === BannerContainerLayout.Horizontal &&
      (position === BannerContainerPosition.BottomMiddle || position === BannerContainerPosition.Center)) ||
    isCorner
  const reverseButtons = layout === BannerContainerLayout.Horizontal && !stackButtons

  const primaryStyle = getIn(themeConfig, themeUpsertFormKeys.bannerPrimaryButtonStyle)
  const primaryBackgroundColor = getIn(themeConfig, themeUpsertFormKeys.bannerPrimaryButtonBackgroundColor)
  const primaryTextColor = getIn(themeConfig, themeUpsertFormKeys.bannerPrimaryButtonTextColor)
  const primaryCornerRadius = clamp(
    getIn(themeConfig, themeUpsertFormKeys.bannerPrimaryButtonCornerRadius),
    BUILDER_CONSTANTS.MIN_BORER_RADIUS,
    BUILDER_CONSTANTS.MAX_BORDER_RADIUS,
  )

  const secondaryStyle = getIn(themeConfig, themeUpsertFormKeys.bannerSecondaryButtonStyle)
  const secondaryBackgroundColor = getIn(themeConfig, themeUpsertFormKeys.bannerSecondaryButtonBackgroundColor)
  const secondaryTextColor = getIn(themeConfig, themeUpsertFormKeys.bannerSecondaryButtonTextColor)
  const secondaryCornerRadius = clamp(
    getIn(themeConfig, themeUpsertFormKeys.bannerSecondaryButtonCornerRadius),
    BUILDER_CONSTANTS.MIN_BORER_RADIUS,
    BUILDER_CONSTANTS.MAX_BORDER_RADIUS,
  )

  const tertiaryStyle = getIn(themeConfig, themeUpsertFormKeys.bannerTertiaryButtonStyle)
  const tertiaryBackgroundColor = getIn(themeConfig, themeUpsertFormKeys.bannerTertiaryButtonBackgroundColor)
  const tertiaryTextColor = getIn(themeConfig, themeUpsertFormKeys.bannerTertiaryButtonTextColor)
  const tertiaryCornerRadius = clamp(
    getIn(themeConfig, themeUpsertFormKeys.bannerTertiaryButtonCornerRadius),
    BUILDER_CONSTANTS.MIN_BORER_RADIUS,
    BUILDER_CONSTANTS.MAX_BORDER_RADIUS,
  )

  return (
    <ButtonsBox
      maxWidth={buttonsBoxPositionMaxWidthMap[position]}
      flexDirection={stackButtons ? 'column' : reverseButtons ? 'row-reverse' : 'row'}
    >
      <ButtonBox
        sx={{
          ...(isCorner ? { width: '100%' } : {}),
          border: `1.5px solid ${primaryBackgroundColor}`,
          background: primaryStyle === ItemStyle.Filled ? primaryBackgroundColor : '',
          borderRadius: primaryCornerRadius + 'px',
        }}
      >
        <Typography variant={'label'} color={primaryTextColor}>
          {experienceEnglishConfig.banner?.buttons?.primary?.text}
        </Typography>
      </ButtonBox>

      {experienceLayoutConfig.banner?.buttons?.secondary?.visible ? (
        <ButtonBox
          sx={{
            ...(isCorner ? { width: '100%' } : {}),
            border: `1.5px solid ${secondaryBackgroundColor}`,
            background: secondaryStyle === ItemStyle.Filled ? secondaryBackgroundColor : '',
            borderRadius: secondaryCornerRadius + 'px',
          }}
        >
          <Typography variant={'label'} color={secondaryTextColor}>
            {experienceEnglishConfig.banner?.buttons?.secondary?.text}
          </Typography>
        </ButtonBox>
      ) : (
        <SectionEmptyState section={'Button'} width={'105px'} justifyContent={'center'} compact />
      )}

      {experienceLayoutConfig.banner?.buttons?.tertiary?.visible ? (
        <ButtonBox
          sx={{
            ...(isCorner ? { width: '100%' } : {}),
            border: `1.5px solid ${tertiaryBackgroundColor}`,
            background: tertiaryStyle === ItemStyle.Filled ? tertiaryBackgroundColor : '',
            borderRadius: tertiaryCornerRadius + 'px',
          }}
        >
          <Typography variant={'label'} color={tertiaryTextColor}>
            {experienceEnglishConfig.banner?.buttons?.tertiary?.text}
          </Typography>
        </ButtonBox>
      ) : (
        <SectionEmptyState section={'Button'} width={'105px'} justifyContent={'center'} compact />
      )}
    </ButtonsBox>
  )
}
